import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal } from 'react-bootstrap'
import styles from './osFieldPickerModal.module.css'

type Props = {
  show: boolean
  title: string
  icon: React.ReactNode
  searchPlaceholder?: string
  currentId?: string | null
  labelKey: string
  sublabelKey?: string
  loadItems: () => Promise<any[]>
  onConfirm: (id: string) => Promise<void>
  handleClose: () => void
}

const modalsRoot = document.getElementById('root-modals') || document.body

const normalize = (value: any) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

/** Mini modal para trocar um vínculo da O.S (cliente, usina, unidade, colaborador). */
export function OSFieldPickerModal({
  show,
  title,
  icon,
  searchPlaceholder = 'Buscar...',
  currentId,
  labelKey,
  sublabelKey,
  loadItems,
  onConfirm,
  handleClose,
}: Props) {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')
  const [selectedId, setSelectedId] = useState<string | null>(currentId ?? null)

  useEffect(() => {
    if (!show) return
    setSearch('')
    setSelectedId(currentId ?? null)
    setLoading(true)
    loadItems()
      .then((result) => setItems(Array.isArray(result) ? result : result ? [result] : []))
      .catch((error) => {
        console.log(error)
        setItems([])
      })
      .finally(() => setLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const filteredItems = useMemo(() => {
    const term = normalize(search.trim())
    const list = term
      ? items.filter((item) =>
          normalize(item?.[labelKey]).includes(term) ||
          (sublabelKey ? normalize(item?.[sublabelKey]).includes(term) : false)
        )
      : items
    // Mantém o item vinculado no topo da lista.
    return [...list].sort((a, b) => (a?.id === currentId ? -1 : b?.id === currentId ? 1 : 0))
  }, [items, search, labelKey, sublabelKey, currentId])

  const hasChanged = !!selectedId && selectedId !== currentId

  const confirm = async () => {
    if (!selectedId || !hasChanged) return
    try {
      setSaving(true)
      await onConfirm(selectedId)
      handleClose()
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName='mw-450px'
      backdrop={true}
    >
      <div className='modal-header py-4'>
        <h3 className='d-flex align-items-center gap-2 mb-0 fw-bold fs-4'>
          <span className='symbol symbol-35px flex-shrink-0'>
            <span className='symbol-label bg-light border border-gray-300'>{icon}</span>
          </span>
          {title}
        </h3>
        <button type='button' className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
          <i className='bi bi-x-lg fs-4' />
        </button>
      </div>

      <div className='modal-body py-4'>
        <div className='input-group input-group-sm mb-3'>
          <span className='input-group-text'><i className='bi bi-search' /></span>
          <input
            type='text'
            className='form-control'
            placeholder={searchPlaceholder}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />
        </div>

        <div className={styles.list}>
          {loading && (
            <div className='d-flex justify-content-center py-6'>
              <span className='spinner-border text-primary' />
            </div>
          )}

          {!loading && filteredItems.length === 0 && (
            <div className='text-center text-muted fw-semibold py-6'>Nenhum item encontrado.</div>
          )}

          {!loading && filteredItems.map((item) => {
            const isSelected = item?.id === selectedId
            const isCurrent = item?.id === currentId
            return (
              <button
                key={item.id}
                type='button'
                className={`${styles.item} ${isSelected ? styles.itemSelected : ''}`}
                onClick={() => setSelectedId(item.id)}
              >
                <span className={styles.check}>
                  {isSelected && <i className='bi bi-check-lg' />}
                </span>
                <span className={styles.texts}>
                  <span className={styles.label}>{item?.[labelKey] || 'Sem nome'}</span>
                  {sublabelKey && item?.[sublabelKey] && (
                    <span className={styles.sublabel}>{item[sublabelKey]}</span>
                  )}
                </span>
                {isCurrent && <span className='badge badge-light-primary fs-8'>Atual</span>}
              </button>
            )
          })}
        </div>
      </div>

      <div className='modal-footer py-3'>
        <button type='button' className='btn btn-sm btn-light' onClick={handleClose}>Cancelar</button>
        <button
          type='button'
          className='btn btn-sm btn-primary'
          disabled={!hasChanged || saving}
          onClick={confirm}
        >
          {saving ? <span className='spinner-border spinner-border-sm me-2' /> : <i className='bi bi-check2 me-1' />}
          Salvar
        </button>
      </div>
    </Modal>,
    modalsRoot
  )
}
